/**
 * Movers — port of app/callbacks/misc.update_movers.
 * Top gainers / losers from a wide-cap universe. Lightweight version:
 * we ride the existing watchlist quotes rather than running a separate
 * universe scan (the Python version queries a hardcoded MOVERS list).
 */
import { useStore } from '@/store';
import { GRN, RED, MUT, FONT, TXT } from '@/theme';

export function Movers() {
  const watchlist = useStore(s => s.watchlist);
  const quotes    = useStore(s => s.quotes);

  // Compute live mover ranking off whatever quotes are currently cached.
  const rows = watchlist
    .map(sym => quotes[sym])
    .filter((q): q is NonNullable<typeof q> => !!q && Number.isFinite(q.change_pct))
    .sort((a, b) => b.change_pct - a.change_pct);

  // slice(0,3) and slice(-3) OVERLAP whenever fewer than 6 quotes have
  // loaded, so the same symbol appeared as both a gainer and a loser —
  // and a "LOSER" printed in red could have a positive change_pct.
  // Partition by sign first, then take the extremes of each side.
  const up   = rows.filter(q => q.change_pct > 0);
  const down = rows.filter(q => q.change_pct < 0);
  const gainers = up.slice(0, 3);
  const losers  = down.slice(-3).reverse();

  const row = (q: typeof rows[number], clr: string) => {
    // A quote can carry a finite change_pct (so it passes the filter) while
    // both last and mid are null on a thin book -- (q.last ?? q.mid).toFixed()
    // would crash. Coerce only a real number to a price; otherwise show a dash
    // rather than fabricating $0 (Number(null) === 0).
    const raw = q.last ?? q.mid;
    const px = typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
    return (
      <div key={q.symbol} style={{
        display: 'flex', justifyContent: 'space-between',
        padding: '2px 4px', fontSize: 10, fontFamily: FONT,
      }}>
        <span style={{ color: TXT, minWidth: 52 }}>{q.symbol}</span>
        <span style={{ color: MUT }}>{px !== null ? `$${px.toFixed(2)}` : '—'}</span>
        <span style={{ color: clr, minWidth: 54, textAlign: 'right' }}>
          {q.change_pct >= 0 ? '+' : ''}{q.change_pct.toFixed(2)}%
        </span>
      </div>
    );
  };

  return (
    <div style={{ borderTop: '1px solid #1e1e1e', marginTop: 'auto', maxHeight: 160, overflowY: 'auto' }}>
      {/* This is a re-sort of the user's OWN watchlist, not a market-wide
          movers scan. "MOVERS" implied the latter. */}
      <div className="pane-hdr"><span>WATCHLIST MOVERS</span></div>
      <div style={{ color: GRN, fontSize: 9, letterSpacing: 1.5, fontFamily: FONT, padding: '2px 4px' }}>
        GAINERS
      </div>
      {gainers.map(q => row(q, GRN))}
      <div style={{ color: RED, fontSize: 9, letterSpacing: 1.5, fontFamily: FONT, padding: '4px 4px 2px' }}>
        LOSERS
      </div>
      {losers.map(q => row(q, RED))}
    </div>
  );
}
